import React, { useState, useRef, useEffect } from "react";
import { getCategories } from "../../utils/storage";
import { IconButton, InputBase } from "@mui/material";

import { Link, useNavigate, useLocation } from "react-router-dom";

import SearchIcon from "@mui/icons-material/Search";
import EmailIcon from "@mui/icons-material/Email";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";

import Logo from "../assets/logo.png";

import UserDropdown from "./UserDropdown";

import "../styles/Header.css";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  const [openProduct, setOpenProduct] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  /* ── SCROLL ─────────────────────────────────────────────── */

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /* ── SEARCH ─────────────────────────────────────────────── */

  const [searchQuery, setSearchQuery] = useState("");

  /* ── ACTIVE NAV ─────────────────────────────────────────── */

  const isActive = (path) => location.pathname === path;

  /* ── SYNC SEARCH ────────────────────────────────────────── */

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q") || "";
    setSearchQuery(q);
  }, [location]);

  /* ── CLOSE MOBILE MENU ON ROUTE CHANGE ─────────────────── */

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname]);

  /* ── SEARCH HANDLERS ────────────────────────────────────── */

  const handleSearch = () => {
    const q = searchQuery.trim();

    if (!q) return;

    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSearchOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      {/* ── HEADER WRAPPER ────────────────────────────────── */}

      <div className={`header-sticky${scrolled ? " scrolled" : ""}`}>
        {/* ── TOP BAR ─────────────────────────────────────── */}

        <div className="top-bar">
          <div style={{ width: 120 }} className="top-bar-spacer" />

          <div className="top-bar-center">
            🚚 Free Standard Shipping on all products · Minimum order ₹1,000
          </div>

          <div className="top-bar-subscribe">
            <EmailIcon style={{ fontSize: 14 }} />
            Subscribe
          </div>
        </div>

        {/* ── MAIN HEADER ─────────────────────────────────── */}

        <div className="main-header">
          {/* ── HAMBURGER (mobile only) ───────────────────── */}

          <IconButton
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            size="small"
          >
            {mobileMenuOpen ? (
              <CloseIcon style={{ color: "#1B211A" }} />
            ) : (
              <MenuIcon style={{ color: "#1B211A" }} />
            )}
          </IconButton>

          {/* ── LOGO ─────────────────────────────────────── */}

          <Link to="/" className="logo-wrap">
            <img src={Logo} alt="Logo" className="logo-img" />
          </Link>

          {/* ── SEARCH (hidden on mobile, shown on tablet+) ── */}

          <div className="search-wrap">
            <InputBase
              placeholder="Search products, brands, categories..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              inputProps={{
                style: {
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13.5,
                  color: "#111010",
                },
              }}
            />

            <IconButton className="search-btn" onClick={handleSearch}>
              <SearchIcon
                style={{
                  fontSize: 20,
                  color: "#14302A",
                }}
              />
            </IconButton>
          </div>

          {/* ── ACTIONS ──────────────────────────────────── */}

          <div className="header-actions">
            {/* ── MOBILE SEARCH TOGGLE ──────────────────── */}

            <IconButton
              className="mobile-search-btn"
              onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
              size="small"
            >
              <SearchIcon style={{ color: "#1B211A", fontSize: 22 }} />
            </IconButton>

            {/* ── USER DROPDOWN ─────────────────────────── */}

            <UserDropdown />
          </div>
        </div>

        {/* ── MOBILE SEARCH BAR ───────────────────────────── */}

        {mobileSearchOpen && (
          <div className="mobile-search-bar">
            <div className="mobile-search-inner">
              <InputBase
                placeholder="Search products, brands..."
                className="mobile-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                inputProps={{
                  style: {
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 14,
                    color: "#111010",
                  },
                }}
              />
              <IconButton
                className="mobile-search-submit"
                onClick={handleSearch}
              >
                <SearchIcon style={{ fontSize: 20, color: "#14302A" }} />
              </IconButton>
            </div>
          </div>
        )}

        {/* ── NAVIGATION (desktop) ────────────────────────── */}

        <nav className="nav-bar">
          <Link
            to="/"
            className={`nav-link${isActive("/") ? " active" : ""}`}
          >
            Home
          </Link>

          <span className="nav-sep" />

          <ProductDropdown
            open={openProduct}
            setOpen={setOpenProduct}
          />

          <span className="nav-sep" />

          <Link
            to="/about-us"
            className={`nav-link${isActive("/about-us") ? " active" : ""}`}
          >
            About Us
          </Link>

          <span className="nav-sep" />

          <Link
            to="/contact"
            className={`nav-link${isActive("/contact") ? " active" : ""}`}
          >
            Contact
          </Link>
        </nav>

        {/* ── MOBILE NAV DRAWER ───────────────────────────── */}

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <Link
              to="/"
              className={`mobile-nav-link${isActive("/") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>

            <MobileProductDropdown onClose={() => setMobileMenuOpen(false)} />

            <Link
              to="/about-us"
              className={`mobile-nav-link${isActive("/about-us") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>

            <Link
              to="/contact"
              className={`mobile-nav-link${isActive("/contact") ? " active" : ""}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        )}
      </div>
    </>
  );
}

/* ── DESKTOP PRODUCT DROPDOWN ───────────────────────────────── */

const ProductDropdown = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadCategories = () => {
      setCategories(getCategories());
    };

    loadCategories();

    window.addEventListener("categoriesUpdated", loadCategories);

    return () =>
      window.removeEventListener("categoriesUpdated", loadCategories);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handler);
    }

    return () => document.removeEventListener("mousedown", handler);
  }, [open, setOpen]);

  return (
    <div className="dropdown-wrap" ref={wrapRef}>
      <div
        className={`dropdown-trigger${open ? " open" : ""}`}
        onClick={() => setOpen(!open)}
      >
        Product Group
        <ExpandMoreIcon
          style={{
            fontSize: 18,
            transition: "transform .25s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </div>

      {open && (
        <div className="mega-menu">
          <div className="mega-menu-cols">
            {categories.map((category) => (
              <div
                key={category.id}
                className="mega-menu-link"
                onClick={() => {
                  navigate(`/category/${category.slug}`);
                  setOpen(false);
                }}
              >
                <span className="mega-menu-link-label">
                  {category.name}
                </span>

                <i className="ti ti-chevron-right mega-menu-link-arrow" />
              </div>
            ))}
          </div>

          <div className="mega-menu-featured">
            <div className="mega-menu-tag">
              Top Industrial Products
            </div>

            <div className="mega-menu-title">
              Premium HVAC Solutions
            </div>

            <div className="mega-menu-desc">
              Explore premium HVAC, electrical &
              industrial solutions engineered for
              reliability and performance.
            </div>

            <Link
              to="/products"
              className="mega-menu-btn"
              onClick={() => setOpen(false)}
            >
              Explore Products →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── MOBILE PRODUCT DROPDOWN ────────────────────────────────── */

const MobileProductDropdown = ({ onClose }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const loadCategories = () => setCategories(getCategories());
    loadCategories();
    window.addEventListener("categoriesUpdated", loadCategories);
    return () => window.removeEventListener("categoriesUpdated", loadCategories);
  }, []);

  return (
    <div className="mobile-nav-group">
      <div
        className={`mobile-nav-link mobile-nav-toggle${open ? " open" : ""}`}
        onClick={() => setOpen(!open)}
      >
        Product Group
        <ExpandMoreIcon
          style={{
            fontSize: 18,
            transition: "transform .25s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            marginLeft: "auto",
          }}
        />
      </div>

      {open && (
        <div className="mobile-nav-submenu">
          {categories.map((category) => (
            <div
              key={category.id}
              className="mobile-nav-sublink"
              onClick={() => {
                navigate(`/category/${category.slug}`);
                onClose();
              }}
            >
              {category.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};